import {
  GridColumnVisibilityModel,
  GridSortModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import {
  FilterKey,
  OmnisearchGridColDef,
  useOmnisearchDatagrid,
} from "hooks/useOmnisearchDatagrid";
import { ReactNode, useMemo } from "react";

import { OmnisearchDataProvider, OmnisearchGridState } from "./OmnisearchDataContext";

/** @deprecated Use `OmnisearchDataGridV2` and `useOmnisearchState` from `hooks/omnisearch-v2` instead. */
export interface OmnisearchProviderProps<Row extends GridValidRowModel> {
  children: ReactNode;
  /** Key used for localStorage persistence of grid settings */
  persistenceKey: string;
  /** Column definitions for the grid */
  columns: OmnisearchGridColDef<Row>[];
  /** Whether to sync omnisearch with URL query params. Default: true */
  isURLDriven?: boolean;
  /** Initial filters to apply */
  initialFilters?: { field: string; value: string | boolean }[];
  /** Initial page size. Default: 25 */
  initialPageSize?: number;
  /** Initial sort model */
  initialSortModel?: GridSortModel;
  /** Initial column visibility */
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
  /** Key aliases for omnisearch parsing (e.g., { tags: "facilityTags" }) */
  keyAliases?: Record<string, string>;
  /** Additional filter keys not derived from columns */
  additionalFilters?: FilterKey[];
  /** Column fields to exclude from filters */
  excludeFilters?: string[];
  /** Whether viewing on mobile */
  isMobile?: boolean;
  /** When true, the omnisearch filter string is persisted to localStorage and
   *  restored on fresh page loads that have no URL omnisearch param. */
  persistOmnisearch?: boolean;
}

/**
 * Provider component that owns omnisearch and grid state.
 *
 * Place this at the highest level that needs omnisearch access.
 * Child components use useOmnisearch() for omnisearch state
 * and useOmnisearchGrid() for grid-specific state.
 *
 * This follows the idiomatic React pattern where:
 * - Provider creates and owns state
 * - Consumers read state via hooks
 * - Single source of truth (no duplicate hook calls)
 *
 * @example
 * <OmnisearchProvider persistenceKey="my-grid" columns={columns} isURLDriven>
 *   <FilterBar />           // Can use useOmnisearch()
 *   <Charts />              // Can use useOmnisearch()
 *   <OmnisearchDataGrid />  // Uses useOmnisearchGrid(), doesn't create own state
 * </OmnisearchProvider>
 *
 * @deprecated Use `OmnisearchDataGridV2` and `useOmnisearchState` from `hooks/omnisearch-v2` instead.
 */
export function OmnisearchProvider<Row extends GridValidRowModel>({
  children,
  persistenceKey,
  columns,
  isURLDriven = true,
  initialFilters,
  initialPageSize,
  initialSortModel,
  initialColumnVisibilityModel,
  keyAliases,
  additionalFilters,
  excludeFilters,
  isMobile,
  persistOmnisearch,
}: OmnisearchProviderProps<Row>) {
  // This is the SINGLE hook call that owns all state
  const {
    // Omnisearch state
    omnisearch,
    setOmnisearch,
    setOmnisearchImmediate,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
    getOmnisearchValue,
    createUrlFromOmnisearchKeyValues,
    // Grid state
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    resetSortModel,
    columns: processedColumns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    setColumnWidthModel,
    unsetColumnWidthModel,
    resetColumnSettings,
    columnFilterKeys,
  } = useOmnisearchDatagrid({
    persistenceKey,
    columns,
    isURLDriven,
    initialFilters,
    initialPageSize,
    initialSortModel,
    initialColumnVisibilityModel,
    additionalFilters,
    excludeFilters,
    isMobile,
    persistOmnisearch,
  });

  // Memoize grid state to prevent unnecessary re-renders
  const gridState = useMemo<OmnisearchGridState>(
    () => ({
      paginationModel,
      setPaginationModel,
      sortModel,
      setSortModel,
      resetSortModel,
      columns: processedColumns,
      columnVisibilityModel,
      setColumnVisibilityModel,
      setColumnWidthModel,
      unsetColumnWidthModel,
      resetColumnSettings,
      columnFilterKeys,
    }),
    [
      paginationModel,
      setPaginationModel,
      sortModel,
      setSortModel,
      resetSortModel,
      processedColumns,
      columnVisibilityModel,
      setColumnVisibilityModel,
      setColumnWidthModel,
      unsetColumnWidthModel,
      resetColumnSettings,
      columnFilterKeys,
    ]
  );

  return (
    <OmnisearchDataProvider
      omnisearch={omnisearch}
      setOmnisearch={setOmnisearch}
      setOmnisearchImmediate={setOmnisearchImmediate}
      editOmnisearchKeyValues={editOmnisearchKeyValues}
      removeOmnisearchKeyValue={removeOmnisearchKeyValue}
      getOmnisearchValue={getOmnisearchValue}
      createUrlFromOmnisearchKeyValues={createUrlFromOmnisearchKeyValues}
      keyAliases={keyAliases}
      gridState={gridState}
    >
      {children}
    </OmnisearchDataProvider>
  );
}
